import React from "react";

const WarrantyPolicy = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen py-16 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-900 shadow-xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
        {/* Header Section */}
        <div className="bg-slate-900 dark:bg-slate-800 py-12 px-8 text-center text-white border-b dark:border-slate-700">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase underline decoration-2 underline-offset-8 mb-4">
            Warranty Policy
          </h1>
          <p className="text-lg font-semibold text-slate-300">
           Brightex Solutions Pvt Ltd
          </p>
          <p className="text-sm text-slate-400 mt-2 tracking-widest uppercase">
            Effective Date: February 2026
          </p>
        </div>

        {/* Content Body */}
        <div className="p-8 md:p-12 space-y-10 text-gray-700 dark:text-gray-300 leading-relaxed">
          {/* Section 1 */}
          <section>
            <h3 className="flex items-center text-xl font-bold text-slate-900 dark:text-gray-100 mb-4 border-l-4 border-slate-900 dark:border-gray-400 pl-4">
              1. WARRANTY OVERVIEW
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-8">
              At lapzop.com, we stand behind the quality of our refurbished
              products. All laptops sold by Brightex Solutions Private Limited
              undergo a rigorous multi-point quality check. This Limited
              Warranty ensures your device is protected against manufacturing
              defects and hardware failures.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-slate-50 dark:bg-gray-800 p-6 rounded-xl border border-slate-100 dark:border-gray-700">
            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 mb-4">
              2. WARRANTY PERIOD
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <span className="text-slate-900 dark:text-slate-200">•</span>
                <span>
                  <strong className="text-slate-900 dark:text-slate-200">Standard Coverage:</strong>{" "}
                  Every laptop comes with a 6-Month Limited Warranty starting
                  from the date of delivery.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-slate-900 dark:text-slate-200">•</span>
                <span>
                  <strong className="text-slate-900 dark:text-slate-200">Accessories:</strong>{" "}
                  Chargers and power adapters provided with the laptop carry a
                  30-day replacement warranty.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 mb-4 border-l-4 border-slate-900 dark:border-gray-400 pl-4">
              3. WHAT IS COVERED
            </h3>
            <p className="mb-4 text-gray-600 dark:text-gray-400">
              This warranty covers the following hardware components under
              normal usage conditions:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                <strong className="text-slate-900 dark:text-slate-200 block mb-1">
                  Motherboard
                </strong>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Internal circuit failures or chipset issues.
                </span>
              </div>
              <div className="p-4 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                <strong className="text-slate-900 dark:text-slate-200 block mb-1">Display</strong>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Screen flickering or dead pixels (more than 3).
                </span>
              </div>
              <div className="p-4 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                <strong className="text-slate-900 dark:text-slate-200 block mb-1">
                  RAM & Storage
                </strong>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Failure of memory modules or SSD/HDD.
                </span>
              </div>
              <div className="p-4 border border-gray-100 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                <strong className="text-slate-900 dark:text-slate-200 block mb-1">
                  Keyboard & Trackpad
                </strong>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Functional failure of keys or touch response.
                </span>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-red-50 dark:bg-red-950/30 p-6 rounded-xl border border-red-100 dark:border-red-900">
            <h3 className="text-xl font-bold text-red-900 dark:text-red-400 mb-4">
              4. WHAT IS NOT COVERED
            </h3>
            <p className="mb-4 text-red-800 font-medium italic">
              The warranty shall be rendered <strong>void</strong> under the
              following circumstances:
            </p>
            <ul className="grid grid-cols-1 gap-3">
              {[
                {
                  title: "Physical Damage",
                  desc: "Any signs of drops, cracks, or external pressure.",
                },
                {
                  title: "Liquid Damage",
                  desc: "Spills or exposure to moisture/corrosion.",
                },
                {
                  title: "Software Issues",
                  desc: "Operating system crashes, viruses, or third-party software compatibility.",
                },
                {
                  title: "Consumables (Battery)",
                  desc: "As these are refurbished units, batteries are guaranteed to have a minimum 1-hour backup at the time of sale. Normal capacity degradation over time is not covered.",
                },
                {
                  title: "Unauthorized Repair",
                  desc: "Any attempt to open the laptop or repair it via a third-party technician without prior approval.",
                },
                {
                  title: "Seal Tampering",
                  desc: 'Removal or breakage of the "Warranty Void if Tampered" sticker.',
                },
              ].map((item, idx) => (
                <li key={idx} className="flex gap-3 text-red-900/80 dark:text-red-300/80">
                  <span className="font-bold">✕</span>
                  <span>
                    <strong className="text-red-900 dark:text-red-400">{item.title}:</strong>{" "}
                    {item.desc}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h3 className="text-xl font-bold text-slate-900 dark:text-gray-100 mb-6 border-l-4 border-slate-900 dark:border-gray-400 pl-4">
              5. WARRANTY CLAIM PROCESS
            </h3>
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
              {[
                {
                  step: "1",
                  title: "Notification",
                  content:
                    "Email Jplapzop@gmail.com ,btxinwardoutward@gmail.com with your order ID and a description or video of the issue.",
                },
                {
                  step: "2",
                  title: "Remote Diagnostics",
                  content:
                    "Our technical team will first attempt to resolve the issue via remote support.",
                },
                {
                  step: "3",
                  title: "Shipment for Repair",
                  content:
                    "If the issue persists, you must ship the device to our Bengaluru service center.",
                },
                {
                  step: "4",
                  title: "Resolution",
                  content:
                    "We will repair the device, replace the defective part, or provide a replacement unit of equal value if the original is beyond repair.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-bold shadow-sm z-10 shrink-0 md:order-1">
                    {item.step}
                  </div>
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded border border-slate-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="font-bold text-slate-900 dark:text-slate-200 mb-1">
                      {item.title}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{item.content}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 6 & 7 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-gray-100 dark:border-gray-700">
            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                6. LIMITATION OF LIABILITY
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Brightex Solutions Private Limited is not responsible for any
                data loss during the repair process. Customers are strictly
                advised to
                <strong className="text-slate-900 dark:text-slate-200"> back up all data </strong>
                before sending the device for service.
              </p>
            </section>
            <section>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                7. GOVERNING LAW
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This warranty is governed by the laws of India, with legal
                jurisdiction exclusively in Bengaluru, Karnataka.
              </p>
            </section>
          </div>
        </div>

        {/* Footer / Contact Section */}
        <footer className="bg-slate-50 dark:bg-gray-800 border-t border-slate-200 dark:border-gray-700 p-8 md:p-12">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 uppercase tracking-tight">
            8. Contact Information
          </h3>
          <div className="space-y-2 text-slate-600 dark:text-slate-300">
            <p className="font-bold text-slate-900 dark:text-slate-200">
Brightex Solutions Pvt Ltd
            </p>
            <p className="text-sm leading-relaxed max-w-md">
              <span className="font-semibold text-slate-700 dark:text-slate-400">
                Service Center:
              </span>{" "}
              No 5 and 6, Krishnappa Compound, Jaraganahally, JP Nagar 7th
              Phase, Kanakapura Main Road, Bengaluru, Karnataka, 560078
            </p>
            <div className="pt-2 text-sm">
              <span className="font-semibold text-slate-700 dark:text-slate-400">GSTIN:</span>{" "}
              29AAICB6992P1ZL
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WarrantyPolicy;
